/**
 * @fileoverview useBandHeightProperty — the update band tells the page how tall it is
 * (WEB-APP-VERSION-REFRESH-001 U4d; coordinator ruling Q-U4D-PLACEMENT).
 * @package @puffin-core/auth-shell
 *
 * `<BuildUpdateNotice>` mounts once per React root, in document flow at the top of the page, so it pushes the page
 * down instead of covering it. Two things cannot simply be pushed:
 *
 *   - a scroll-locked, full-height layout (`.app-layout`, `.auth-layout`): its last rows would land below a fold the
 *     page cannot scroll to;
 *   - chrome pinned to the viewport's top (`.app-layout-mobile-header`): it would draw over the band.
 *
 * So the band publishes its MEASURED height as `--build-update-band-height` on the document element, and
 * `@puffin-core/shared-styles` subtracts it from those layouts and adds it to that chrome. Measured, never a constant:
 * the band is one line on a desktop and wraps to two or three on a phone, and its waiting line comes and goes.
 *
 * Used as the band's `ref`: a callback ref whose cleanup removes the property when the band goes. React 19 runs that
 * cleanup instead of calling the ref with `null`, so the ref only ever sees the element.
 *
 * @module @puffin-core/auth-shell/hooks/useBandHeightProperty
 */

import { useCallback } from 'react';

/** The custom property shared-styles reads (`var(--build-update-band-height, 0px)`). */
export const BUILD_UPDATE_BAND_HEIGHT_PROPERTY = '--build-update-band-height';

/**
 * @returns {(node: HTMLElement) => (() => void)} a callback ref for the band's element
 */
export function useBandHeightProperty() {
  return useCallback((node) => {
    const page = node.ownerDocument.documentElement;
    const publish = () => {
      page.style.setProperty(
        BUILD_UPDATE_BAND_HEIGHT_PROPERTY,
        `${node.getBoundingClientRect().height}px`,
      );
    };
    publish();
    // The page's own ResizeObserver (absent in an old browser: the height is then published once).
    const { ResizeObserver: PageResizeObserver } = node.ownerDocument.defaultView;
    const observer =
      typeof PageResizeObserver === 'function' ? new PageResizeObserver(publish) : null;
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      page.style.removeProperty(BUILD_UPDATE_BAND_HEIGHT_PROPERTY);
    };
  }, []);
}
